import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Phone, RefreshCw, Smartphone, AlertTriangle, CheckCircle, Loader2, QrCode } from "lucide-react";
import { KioskViewport } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";
import axios from "axios";
import QRCode from "qrcode";

const API = '/api';
const IDLE_TIMEOUT = 120; // Longer timeout for QR payment - customer needs time to scan and pay
const POLL_INTERVAL = 3000; // Poll every 3 seconds
const MAX_POLL_TIME = 900000; // 15 minutes max polling time

/**
 * KioskPayQR - QR Code Payment Display Screen
 * 
 * FLOW:
 * 1. Receives session data from KioskPayQREntry via navigation state
 * 2. Calls POST /api/qr/session to create Stripe Checkout Session
 * 3. Generates QR code from checkout URL
 * 4. Polls GET /api/qr/status/{checkout_session_id} for payment completion
 * 5. Shows success when payment is confirmed
 * 
 * MUST NOT:
 * - Call POST /api/terminal/intent
 * - Poll terminal status
 * - Redirect to /kiosk/pay-kiosk
 */
export default function KioskPayQR() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, vehicleReg, amount, durationDisplay } = location.state || {};

  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, processing, succeeded, failed, expired
  const [hasExitAuth, setHasExitAuth] = useState(false);
  
  const idleTimerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollStartTimeRef = useRef(null);

  // Idle timeout - return to attract after extended inactivity
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Check for missing state - redirect to QR entry, NOT to Terminal
  useEffect(() => {
    if (!sessionId || !vehicleReg) {
      // Missing required state - go back to QR entry screen
      navigate('/kiosk/pay-qr-entry', { replace: true });
    }
  }, [sessionId, vehicleReg, navigate]);

  // Create QR session and generate QR code
  const createQRSession = useCallback(async () => {
    if (!sessionId || !vehicleReg) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Create Stripe Checkout Session via QR endpoint
      const response = await axios.post(`${API}/qr/session`, {
        session_id: sessionId,
        origin_url: window.location.origin
      });
      
      if (response.data && response.data.checkout_url) {
        setCheckoutData(response.data);
        
        // Generate QR code from checkout URL
        const qrDataUrl = await QRCode.toDataURL(response.data.qr_data || response.data.checkout_url, {
          width: 400,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
        setQrCodeDataUrl(qrDataUrl);
        
        // Start polling for payment status
        pollStartTimeRef.current = Date.now();
        startPolling(response.data.checkout_session_id);
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (err) {
      console.error("QR session creation error:", err);
      const errorMessage = err.response?.data?.detail || "Unable to create payment QR code. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [sessionId, vehicleReg]);

  // Poll for payment status
  const startPolling = useCallback((checkoutSessionId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      // Check if we've exceeded max poll time
      if (Date.now() - pollStartTimeRef.current > MAX_POLL_TIME) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setPaymentStatus('expired');
        setError("Payment session expired. Please try again.");
        return;
      }
      
      try {
        const statusRes = await axios.get(`${API}/qr/status/${checkoutSessionId}`);
        const data = statusRes.data;
        
        // Update status
        if (data.payment_status === 'completed' || data.status === 'completed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('succeeded');
          setHasExitAuth(data.has_exit_authorisation || false);
        } else if (data.payment_status === 'expired' || data.status === 'expired') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('expired');
          setError("Payment session expired. Please try again.");
        } else if (data.payment_status === 'cancelled' || data.status === 'cancelled') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('failed');
          setError("Payment was cancelled.");
        } else if (data.is_settled && data.has_exit_authorisation) {
          // Payment completed via webhook
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPaymentStatus('succeeded');
          setHasExitAuth(true);
        }
      } catch (err) {
        // Don't fail on single poll error, continue polling
        console.error("Poll error:", err);
      }
    }, POLL_INTERVAL);
  }, []);

  // Initial QR session creation
  useEffect(() => {
    if (sessionId && vehicleReg && !checkoutData) {
      createQRSession();
    }
  }, [sessionId, vehicleReg, checkoutData, createQRSession]);

  const handleRetry = () => {
    setCheckoutData(null);
    setQrCodeDataUrl(null);
    setPaymentStatus('pending');
    setError(null);
    createQRSession();
  };

  const handleBackToEntry = () => {
    // Go back to QR entry, NOT to Terminal
    navigate('/kiosk/pay-qr-entry');
  };

  const formatAmount = (pence) => {
    if (!pence) return "£0.00";
    return `£${(pence / 100).toFixed(2)}`;
  };

  // Don't render if missing required state
  if (!sessionId || !vehicleReg) {
    return null;
  }

  // PAYMENT SUCCESS STATE
  if (paymentStatus === 'succeeded') {
    return (
      <KioskViewport>
        <div 
          data-testid="kiosk-pay-qr"
          style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            background: '#0d2137' 
          }}
        >
          <KioskHeader showDateTime={true} />
          
          <main style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 50%, #86efac 100%)', 
            padding: '48px',
            textAlign: 'center'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '32px',
              padding: '64px 80px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
              maxWidth: '800px'
            }} data-testid="payment-success">
              <CheckCircle size={120} color="#22c55e" style={{ marginBottom: '32px' }} />
              <h1 style={{ fontSize: '52px', fontWeight: '800', color: '#15803d', marginBottom: '16px' }}>
                Payment Successful!
              </h1>
              <p style={{ fontSize: '28px', color: '#166534', marginBottom: '24px' }}>
                {formatAmount(amount)} paid for {vehicleReg}
              </p>
              <div style={{
                background: '#f0fdf4',
                borderRadius: '16px',
                padding: '24px',
                border: '3px solid #86efac'
              }}>
                <p style={{ fontSize: '26px', color: '#15803d', fontWeight: '700', margin: 0 }}>
                  {hasExitAuth 
                    ? "You are now authorised to exit. The barrier will open automatically."
                    : "Your payment has been received. Please drive to the exit."
                  }
                </p>
              </div>
              <button
                onClick={() => navigate('/kiosk/attract')}
                style={{
                  marginTop: '40px',
                  padding: '20px 48px',
                  fontSize: '24px',
                  fontWeight: '700',
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer'
                }}
                data-testid="done-button"
              >
                Done
              </button>
            </div>
          </main>
        </div>
      </KioskViewport>
    );
  }

  // ERROR / EXPIRED STATE
  if (error || paymentStatus === 'expired' || paymentStatus === 'failed') {
    return (
      <KioskViewport>
        <div 
          data-testid="kiosk-pay-qr"
          style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            background: '#0d2137' 
          }}
        >
          <KioskHeader showDateTime={true} />
          
          <main style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)', 
            padding: '32px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '32px',
              padding: '64px 80px',
              boxShadow: '0 16px 64px rgba(0,0,0,0.12)',
              textAlign: 'center',
              maxWidth: '700px'
            }} data-testid="error-state">
              <AlertTriangle size={100} color="#f59e0b" style={{ marginBottom: '32px' }} />
              <h2 style={{ fontSize: '40px', fontWeight: '700', color: '#dc2626', marginBottom: '16px' }}>
                {paymentStatus === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h2>
              <p style={{ fontSize: '24px', color: '#64748b', marginBottom: '40px' }}>
                {error || "Something went wrong with your payment."}
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                <button
                  onClick={handleRetry}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '20px 40px',
                    fontSize: '22px',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    cursor: 'pointer'
                  }}
                  data-testid="retry-button"
                >
                  <RefreshCw size={24} />
                  Try Again
                </button>
                <button
                  onClick={handleBackToEntry}
                  style={{
                    padding: '20px 40px',
                    fontSize: '22px',
                    fontWeight: '700',
                    background: 'white',
                    color: '#1e3a5f',
                    border: '3px solid #1e3a5f',
                    borderRadius: '16px',
                    cursor: 'pointer'
                  }}
                  data-testid="back-button"
                >
                  Start Over
                </button>
              </div>
            </div>

            {/* Help Footer */}
            <div style={{ 
              marginTop: '40px',
              padding: '24px 48px',
              background: '#0d2137',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px'
            }}>
              <Phone size={28} color="#fbbf24" />
              <span style={{ color: 'white', fontWeight: '600', fontSize: '22px' }}>Need help? Call </span>
              <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '28px' }}>01292 833107</span>
            </div>
          </main>
        </div>
      </KioskViewport>
    );
  }

  // LOADING STATE
  if (loading) {
    return (
      <KioskViewport>
        <div 
          data-testid="kiosk-pay-qr"
          style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            background: '#0d2137' 
          }}
        >
          <KioskHeader showDateTime={true} />
          
          <main style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '32px',
              padding: '64px 80px',
              boxShadow: '0 16px 64px rgba(0,0,0,0.12)',
              textAlign: 'center'
            }} data-testid="loading-state">
              <Loader2 size={80} color="#7c3aed" style={{ animation: 'spin 1s linear infinite', marginBottom: '32px' }} />
              <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#7c3aed', marginBottom: '12px' }}>
                Generating QR Code
              </h2>
              <p style={{ fontSize: '22px', color: '#64748b' }}>
                Please wait...
              </p>
            </div>
          </main>
        </div>
        
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </KioskViewport>
    );
  }

  // MAIN QR DISPLAY STATE
  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-pay-qr"
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          background: '#0d2137' 
        }}
      >
        <KioskHeader showDateTime={true} />

        {/* MAIN CONTENT */}
        <main style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)', 
          padding: '24px'
        }}>
          {/* Back Button */}
          <button 
            onClick={handleBackToEntry}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              backgroundColor: '#1e3a5f',
              border: 'none',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              marginBottom: '16px'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={24} color="white" />
            <span style={{ color: 'white', fontWeight: '700', fontSize: '20px' }}>Back</span>
          </button>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h1 style={{ fontSize: '44px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
              Scan to Pay with Your Phone
            </h1>
            <p style={{ fontSize: '22px', color: '#475569' }}>
              Use your phone camera to scan the QR code below
            </p>
          </div>

          {/* QR Code Card */}
          <div style={{ 
            flex: 1,
            display: 'flex',
            gap: '32px',
            background: 'white', 
            borderRadius: '24px', 
            padding: '32px', 
            boxShadow: '0 12px 48px rgba(0,0,0,0.1)'
          }} data-testid="qr-display-card">
            {/* Left Side - QR Code */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <div style={{ 
                background: 'white', 
                padding: '20px', 
                borderRadius: '24px', 
                border: '6px solid #7c3aed',
                boxShadow: '0 8px 32px rgba(124, 58, 237, 0.2)'
              }}>
                {qrCodeDataUrl ? (
                  <img
                    src={qrCodeDataUrl}
                    alt="Payment QR Code"
                    style={{ width: '350px', height: '350px' }}
                    data-testid="qr-code-image"
                  />
                ) : (
                  <div style={{ width: '350px', height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <QrCode size={200} color="#e2e8f0" />
                  </div>
                )}
              </div>
              <p style={{ marginTop: '20px', fontSize: '20px', color: '#64748b', textAlign: 'center' }}>
                Point your phone camera at the QR code
              </p>
              
              {/* Polling indicator */}
              <div style={{
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#7c3aed'
              }}>
                <Loader2 size={20} style={{ animation: 'spin 2s linear infinite' }} />
                <span style={{ fontSize: '16px' }}>Waiting for payment...</span>
              </div>
            </div>

            {/* Right Side - Payment Details & Instructions */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center', 
              gap: '24px' 
            }}>
              {/* Vehicle Info */}
              <div style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
                border: '2px solid #93c5fd'
              }}>
                <p style={{ fontSize: '16px', color: '#1e40af', marginBottom: '4px' }}>Vehicle</p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: '#1e3a5f', margin: 0, letterSpacing: '2px' }}>
                  {vehicleReg}
                </p>
                {durationDisplay && (
                  <p style={{ fontSize: '16px', color: '#64748b', marginTop: '8px' }}>
                    Parked for {durationDisplay}
                  </p>
                )}
              </div>

              {/* Amount */}
              <div style={{ 
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                borderRadius: '20px',
                padding: '28px',
                textAlign: 'center',
                border: '3px solid #86efac'
              }}>
                <p style={{ fontSize: '20px', color: '#166534', marginBottom: '8px' }}>Amount to Pay</p>
                <p style={{ fontSize: '64px', fontWeight: '800', color: '#15803d', margin: 0 }}>
                  {formatAmount(amount || checkoutData?.amount * 100)}
                </p>
              </div>

              {/* Instructions */}
              <div style={{ 
                background: '#f0f9ff', 
                borderRadius: '20px', 
                padding: '24px',
                border: '2px solid #bfdbfe'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Smartphone size={36} color="#3b82f6" />
                  <p style={{ fontSize: '22px', fontWeight: '700', color: '#1e40af', margin: 0 }}>How to pay:</p>
                </div>
                <ol style={{ fontSize: '20px', color: '#334155', margin: 0, paddingLeft: '24px', lineHeight: '2' }}>
                  <li>Open your phone camera</li>
                  <li>Point at the QR code</li>
                  <li>Tap the link that appears</li>
                  <li>Complete payment on your phone</li>
                </ol>
              </div>

              {/* Alternative - Go back to entry, NOT to Terminal */}
              <button 
                onClick={handleBackToEntry}
                style={{ 
                  padding: '16px 28px',
                  fontSize: '18px', 
                  color: '#1e3a5f', 
                  background: 'white', 
                  border: '2px solid #1e3a5f', 
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                ← Change Vehicle Registration
              </button>
            </div>
          </div>

          {/* Help Section */}
          <div style={{ 
            marginTop: '20px',
            padding: '20px 40px',
            background: '#0d2137',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
          }}>
            <Phone size={28} color="#fbbf24" />
            <span style={{ color: 'white', fontWeight: '600', fontSize: '20px' }}>Need help? Call </span>
            <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '26px' }}>01292 833107</span>
          </div>
        </main>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </KioskViewport>
  );
}
